import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import Button from '../../components/atoms/Button';
import { TRANSLATION_KEY } from '../constants';
import { formatScheduleLabel, formatShortScheduleLabel } from '../utils';
import css from './AddMonitorBar.css';

const SCHEDULE_LABEL_PREFIX = 'Checks ';

const AddMonitorBar = ({
    onSubmit,
    scheduleCron,
    onScheduleClick,
    submitting = false,
    error = null,
    isEmptyVariant = false,
    suggestion = null,
    translate,
}) => {
    const [value, setValue] = useState('');

    const valueRef = useRef(value);
    valueRef.current = value;
    const lastSuggestionRef = useRef('');

    useEffect(() => {
        if (!suggestion) return;
        const current = valueRef.current;
        if (!current.trim() || current === lastSuggestionRef.current) {
            setValue(suggestion.text);
            lastSuggestionRef.current = suggestion.text;
        }
    }, [suggestion]);

    const submit = async () => {
        if (!value.trim() || submitting) return;
        if (await onSubmit(value.trim())) setValue('');
    };
    const placeholderKey = isEmptyVariant ? 'EMPTY_STATE.PLACEHOLDER' : 'ADD_BAR.PLACEHOLDER';
    const addKey = isEmptyVariant ? 'EMPTY_STATE.ADD' : 'ADD_BAR.ADD';
    const { label: scheduleLabel, title: scheduleTitle } = formatShortScheduleLabel(scheduleCron);
    const hasRunsPrefix = scheduleLabel.startsWith(SCHEDULE_LABEL_PREFIX);
    const scheduleValue = hasRunsPrefix ? scheduleLabel.slice(SCHEDULE_LABEL_PREFIX.length) : scheduleLabel;

    return (
        <div className={classNames(css.container, { [css.emptyContainer]: isEmptyVariant })}>
            <div className={classNames(css.bar, { [css.emptyVariant]: isEmptyVariant })}>
                <Icon name="sparkles" size={IconSizes.SMALL} interactive={false} />
                <input
                    className={css.input}
                    value={value}
                    placeholder={translate(`${TRANSLATION_KEY}.${placeholderKey}`)}
                    onChange={e => setValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && submit()}
                />
                {!isEmptyVariant && (
                    <button type="button" className={css.scheduleChip} onClick={onScheduleClick}>
                        <Icon name="clock" size={IconSizes.X_SMALL} interactive={false} />
                        {formatScheduleLabel(scheduleCron)}
                    </button>
                )}
                <Button
                    type="link"
                    disabled={!value.trim() || submitting}
                    showSpinner={submitting}
                    onClick={submit}
                    className={css.addMonitorButton}
                >
                    <Translate id={`${TRANSLATION_KEY}.${addKey}`} />
                </Button>
            </div>
            {isEmptyVariant && (
                <button type="button" className={css.scheduleRow} onClick={onScheduleClick} title={scheduleTitle}>
                    <Icon name="clock" size={IconSizes.X_SMALL} interactive={false} />
                    {hasRunsPrefix && <span>{SCHEDULE_LABEL_PREFIX.trim()}</span>}
                    <span className={css.scheduleValue}>{scheduleValue}</span>
                </button>
            )}
            {error && <div className={css.error}>{error}</div>}
        </div>
    );
};

AddMonitorBar.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    scheduleCron: PropTypes.string.isRequired,
    onScheduleClick: PropTypes.func.isRequired,
    submitting: PropTypes.bool,
    error: PropTypes.string,
    isEmptyVariant: PropTypes.bool,
    suggestion: PropTypes.shape({
        text: PropTypes.string.isRequired,
        seq: PropTypes.number.isRequired,
    }),
    translate: PropTypes.func.isRequired,
};

export default withLocalize(AddMonitorBar);
